import classNames from "classnames";
import type { PDFDocumentProxy } from "pdfjs-dist/types/src/display/api";
import {
  memo,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { useInView } from "react-intersection-observer";
import { loadPdfDocument } from "../../../helpers/pdfjs";
import { useObjectUrl } from "./useObjectUrl";

// NOTE: 1 枚あたりのキャンバスが大きくなりすぎないよう、長辺の画素数に上限を設ける。
//       表示枠は最大 800px なので、高精細ディスプレイでも読める解像度は保てる
const PDF_RENDER_MAX_LONG_SIDE_PX = 2048;

// 100% は表示枠の幅に合わせた大きさ（開いた時点の大きさ）とする
const ZOOM_LEVELS = [0.5, 0.75, 1, 1.25, 1.5, 2];
const INITIAL_ZOOM_INDEX = ZOOM_LEVELS.indexOf(1);

type Props = {
  file: File;
  // 文書を開けたとき（全ページ数を知らせる）
  onOpen?: (numPages: number) => void;
  // 1 ページ目を描き終えたとき
  onLoad: () => void;
  // 文書を開けない（パスワード付き・壊れている）か、1 ページ目を最初に描けないとき
  onError: () => void;
  className?: string;
};

// 余白を除いた表示枠の幅。100% のときのページの幅になる
const measureFitWidth = (el: HTMLElement) => {
  const style = window.getComputedStyle(el);
  return (
    el.clientWidth -
    (parseFloat(style.paddingLeft) || 0) -
    (parseFloat(style.paddingRight) || 0)
  );
};

const isScrolledToBottom = (el: HTMLElement) =>
  el.scrollHeight > el.clientHeight &&
  el.scrollTop + el.clientHeight >= el.scrollHeight - 1;

/**
 * PDF の全ページを縦につなげて、ブラウザで PDF を開いたときと同じようにスクロールで見せる
 * - 縮尺は表示幅に合わせ、開いた時点で 1 ページ目の上の部分が読める大きさにする
 * - 見えている範囲のページだけを描き、範囲から外れたページのキャンバスは捨てる
 * - 今見ているページと全ページ数を「1 / 5」の形で示す
 * - 「−」「+」で拡大・縮小できる（50%〜200%）
 */
export const PdfScrollViewer = ({
  file,
  onOpen,
  onLoad,
  onError,
  className,
}: Props) => {
  const url = useObjectUrl(file);
  const [pdfDoc, setPdfDoc] = useState<PDFDocumentProxy | null>(null);
  const [pageAspectRatio, setPageAspectRatio] = useState(1 / Math.SQRT2);
  const [currentPage, setCurrentPage] = useState(1);
  const [scrollRoot, setScrollRoot] = useState<HTMLDivElement | null>(null);
  const [fitWidth, setFitWidth] = useState(0);
  const [zoomIndex, setZoomIndex] = useState(INITIAL_ZOOM_INDEX);
  const scrollRatioRef = useRef<number | null>(null);
  const zoom = ZOOM_LEVELS[zoomIndex];
  const numPages = pdfDoc?.numPages ?? 0;

  // NOTE: 測った後に縦のスクロールバーが出て幅が縮み、100% でも横にはみ出すのを防ぐため、
  //       スクロール領域には scrollbar-gutter: stable を指定してある
  useLayoutEffect(() => {
    if (!scrollRoot) {
      return;
    }
    setFitWidth(measureFitWidth(scrollRoot));
    if (typeof ResizeObserver === "undefined") {
      return;
    }
    // ウィンドウの大きさが変わっても、100% を表示枠の幅に合わせ続ける
    const observer = new ResizeObserver(() =>
      setFitWidth(measureFitWidth(scrollRoot)),
    );
    observer.observe(scrollRoot);
    return () => observer.disconnect();
  }, [scrollRoot]);

  // 拡大・縮小しても、見ていた位置にとどまる
  useLayoutEffect(() => {
    if (!scrollRoot || scrollRatioRef.current === null) {
      return;
    }
    scrollRoot.scrollTop = scrollRatioRef.current * scrollRoot.scrollHeight;
    scrollRatioRef.current = null;
  }, [zoom]);

  const changeZoom = (delta: number) => {
    if (scrollRoot && scrollRoot.scrollHeight > 0) {
      scrollRatioRef.current = scrollRoot.scrollTop / scrollRoot.scrollHeight;
    }
    setZoomIndex((index) =>
      Math.min(Math.max(index + delta, 0), ZOOM_LEVELS.length - 1),
    );
  };

  // 短い最終ページは表示枠の上 1/3 に届かないまま下端に着くので、下端なら最終ページとする
  const handleCurrent = useCallback(
    (pageNumber: number) => {
      setCurrentPage(
        scrollRoot && isScrolledToBottom(scrollRoot) ? numPages : pageNumber,
      );
    },
    [scrollRoot, numPages],
  );
  const handleScroll = () => {
    if (scrollRoot && numPages > 0 && isScrolledToBottom(scrollRoot)) {
      setCurrentPage(numPages);
    }
  };

  useEffect(() => {
    if (!url) {
      return;
    }

    let active = true;
    const loadingTask = loadPdfDocument(url);
    loadingTask.promise
      .then(async (doc) => {
        const firstPage = await doc.getPage(1);
        const viewport = firstPage.getViewport({ scale: 1 });
        if (!active) {
          return;
        }
        setPageAspectRatio(viewport.width / viewport.height);
        setPdfDoc(doc);
        onOpen?.(doc.numPages);
      })
      .catch(() => {
        if (active) {
          onError();
        }
      });

    return () => {
      active = false;
      loadingTask.destroy();
    };
  }, [url]);

  return (
    <div className={classNames("flex min-h-0 flex-col", className)}>
      <div className="flex h-10 shrink-0 items-center justify-center gap-4 bg-black text-sm text-white">
        {pdfDoc && (
          <>
            <span>
              {currentPage} / {numPages}
            </span>
            <span className="h-4 w-px bg-gray-700" />
            <button
              type="button"
              aria-label="縮小"
              onClick={() => changeZoom(-1)}
              disabled={zoomIndex === 0}
              className="cursor-pointer border-0 bg-transparent px-2 text-lg text-white disabled:cursor-default disabled:opacity-40"
            >
              −
            </button>
            <span className="w-12 text-center">{Math.round(zoom * 100)}%</span>
            <button
              type="button"
              aria-label="拡大"
              onClick={() => changeZoom(1)}
              disabled={zoomIndex === ZOOM_LEVELS.length - 1}
              className="cursor-pointer border-0 bg-transparent px-2 text-lg text-white disabled:cursor-default disabled:opacity-40"
            >
              +
            </button>
          </>
        )}
      </div>
      <div
        ref={setScrollRoot}
        onScroll={handleScroll}
        className="relative box-border min-h-0 flex-1 overflow-auto bg-gray-400 px-6 py-4 [scrollbar-gutter:stable]"
      >
        {/* 縮尺を表示幅に合わせるため、表示枠ができてから描く */}
        {pdfDoc &&
          scrollRoot &&
          Array.from({ length: numPages }, (_, i) => (
            <PdfPage
              key={i}
              pdfDoc={pdfDoc}
              pageNumber={i + 1}
              width={(fitWidth || scrollRoot.clientWidth) * zoom}
              zoom={zoom}
              placeholderAspectRatio={pageAspectRatio}
              scrollRoot={scrollRoot}
              onCurrent={handleCurrent}
              // 表示不可の判定は 1 ページ目を最初に描けたかだけで行う。
              // 途中のページや、倍率を変えた後の描き直しの失敗では表示不可にしない
              onFirstRendered={i === 0 ? onLoad : undefined}
              onFirstRenderError={i === 0 ? onError : undefined}
            />
          ))}
      </div>
    </div>
  );
};

type PdfPageProps = {
  pdfDoc: PDFDocumentProxy;
  pageNumber: number;
  // 表示するページの幅（CSS px）
  width: number;
  zoom: number;
  placeholderAspectRatio: number;
  scrollRoot: HTMLDivElement;
  // 表示枠の上 1/3 にかかったとき、今見ているページとして知らせる
  onCurrent: (pageNumber: number) => void;
  // 初めて描き終えたとき
  onFirstRendered?: () => void;
  // 一度も描けないまま失敗したとき
  onFirstRenderError?: () => void;
};

// NOTE: ページ数の表示が変わるたびに全ページを描き直さないよう memo にする
const PdfPage = memo(function PdfPage({
  pdfDoc,
  pageNumber,
  width,
  zoom,
  placeholderAspectRatio,
  scrollRoot,
  onCurrent,
  onFirstRendered,
  onFirstRenderError,
}: PdfPageProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const hasRenderedRef = useRef(false);
  const [aspectRatio, setAspectRatio] = useState<number | null>(null);
  const { ref: nearRef, inView: isNearView } = useInView({
    root: scrollRoot,
    // 表示枠の上下 1 枠分を含めて描いておき、範囲から外れたら捨てる
    rootMargin: "100% 0px",
  });
  // 1 ページ目は、見えているかが分かる前から描き始める
  const shouldRender =
    isNearView || (pageNumber === 1 && !hasRenderedRef.current);
  const { ref: currentRef } = useInView({
    root: scrollRoot,
    rootMargin: "0px 0px -67% 0px",
    onChange: (isCurrent) => {
      if (isCurrent) {
        onCurrent(pageNumber);
      }
    },
  });
  const pageRef = useCallback(
    (el: HTMLDivElement | null) => {
      nearRef(el);
      currentRef(el);
    },
    [nearRef, currentRef],
  );

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!shouldRender) {
      // 範囲から外れたページのキャンバスを捨て、長い PDF でもメモリを使い続けないようにする
      if (canvas) {
        canvas.width = 0;
        canvas.height = 0;
      }
      return;
    }

    let active = true;
    let cancelRender: (() => void) | undefined;

    pdfDoc
      .getPage(pageNumber)
      .then(async (page) => {
        if (!active || !canvas) {
          return;
        }
        const baseViewport = page.getViewport({ scale: 1 });
        const fitWidthScale = width > 0 ? width / baseViewport.width : 1;
        // 拡大したときに粗くならないよう、上限も倍率に合わせて広げる
        const scale = Math.min(
          fitWidthScale * (window.devicePixelRatio || 1),
          (PDF_RENDER_MAX_LONG_SIDE_PX * Math.max(zoom, 1)) /
            Math.max(baseViewport.width, baseViewport.height),
        );
        const viewport = page.getViewport({ scale });
        canvas.width = viewport.width;
        canvas.height = viewport.height;
        const context = canvas.getContext("2d");
        if (context) {
          const renderTask = page.render({
            canvasContext: context,
            viewport,
            canvas,
          });
          cancelRender = () => renderTask.cancel();
          await renderTask.promise;
        }
        if (!active) {
          return;
        }
        setAspectRatio(baseViewport.width / baseViewport.height);
        if (!hasRenderedRef.current) {
          hasRenderedRef.current = true;
          onFirstRendered?.();
        }
      })
      .catch(() => {
        if (active && !hasRenderedRef.current) {
          onFirstRenderError?.();
        }
      });

    return () => {
      active = false;
      cancelRender?.();
    };
  }, [shouldRender, pdfDoc, pageNumber, width, zoom]);

  return (
    <div
      ref={pageRef}
      className="mx-auto mb-4 bg-white shadow-md"
      // キャンバスを捨てても高さが変わらないよう、ページの縦横比で枠の高さを決める
      style={{ width, aspectRatio: aspectRatio ?? placeholderAspectRatio }}
      data-testid={`pdf-page-${pageNumber}`}
    >
      <canvas ref={canvasRef} className="block h-full w-full" />
    </div>
  );
});
